import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { Card, Badge, Empty, Table, Td, Btn } from "../components/UI";
import { SlidePanel } from "../components/SlidePanel";
import { CompanyForm } from "./InsuranceCompanies";
import { useAuth } from "../context/AuthContext";
import { canEdit } from "../lib/permissions";
import { ArrowLeft, Building2, Phone, Printer, Mail, Globe, MapPin, UserRound, Pencil, Send, ShieldCheck, Banknote, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const Info = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="h-9 w-9 rounded-xl bg-[#F5F2EB] flex items-center justify-center text-[#7B6157] shrink-0"><Icon size={16} /></div>
    <div className="min-w-0">
      <div className="text-xs font-bold uppercase tracking-[0.05em] text-[#7B6157]">{label}</div>
      <div className="mt-0.5 text-sm text-[#3E2723] break-words">{value || "-"}</div>
    </div>
  </div>
);

const KV = ({ label, value }) => (
  <div className="min-w-0">
    <div className="text-xs font-bold uppercase tracking-[0.05em] text-[#7B6157]">{label}</div>
    <div className="mt-0.5 text-sm text-[#3E2723] break-words">{value || "-"}</div>
  </div>
);

const YesNoChip = ({ label, value }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${value === "yes" ? "bg-[#E8F5E9] text-[#2E7D32]" : value === "no" ? "bg-[#F5F2EB] text-[#6D4C41]" : "bg-[#F5F2EB] text-[#84675B]"}`}>
    {value === "yes" ? <CheckCircle2 size={13} /> : <XCircle size={13} />} {label}{value ? "" : ": -"}
  </span>
);

export default function InsuranceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canBilling = canEdit(user, "billing");
  const [company, setCompany] = useState(null);
  const [panel, setPanel] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/insurance-companies/${id}`).then((r) => setCompany(r.data));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!company) return <div className="py-20 text-center text-[#84675B]">Loading…</div>;

  const openEdit = () => { setForm(Object.fromEntries(Object.entries(company).map(([k, v]) => [k, v ?? ""]))); setPanel(true); };
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { insured_clients, id: _i, created_at, status, ...body } = form;
      await api.put(`/insurance-companies/${id}`, body);
      toast.success("Company updated");
      setPanel(false); load();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  return (
    <div>
      <button data-testid="back-to-companies-btn" onClick={() => navigate("/insurance-companies")} className="mb-4 flex items-center gap-1 text-sm text-[#7B6157] hover:underline">
        <ArrowLeft size={15} /> All companies
      </button>
      <Card className="p-6 mb-5">
        <div className="flex flex-wrap items-center gap-4">
          <div className="h-14 w-14 rounded-2xl bg-[#4E342E] text-white flex items-center justify-center"><Building2 size={24} /></div>
          <div className="flex-1 min-w-0">
            <h1 className="font-heading text-2xl font-bold text-[#3E2723]" data-testid="company-detail-name">{company.name}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-[#6D4C41]">
              <span>Payer ID: {company.payer_id || "-"}</span>
              {company.payer_type && <span className="rounded-full bg-[#F5F2EB] px-2.5 py-0.5 text-xs font-semibold">{company.payer_type}{company.plan_type ? ` · ${company.plan_type}` : ""}</span>}
              {company.network_status && <span className="rounded-full bg-[#EDE7F6] text-[#5E35B1] px-2.5 py-0.5 text-xs font-semibold">{company.network_status}</span>}
              <Badge value={company.status} />
            </div>
          </div>
          {canBilling && <Btn data-testid="edit-company-detail-btn" variant="outline" className="rounded-lg" onClick={openEdit}><Pencil size={14} /> Edit Company</Btn>}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
        <Card className="p-6 space-y-5" data-testid="company-business-info">
          <h3 className="font-heading text-sm font-bold text-[#3E2723]">Business Information</h3>
          <Info icon={UserRound} label="Contact person" value={company.contact_person} />
          <Info icon={Phone} label="Phone" value={company.phone} />
          <Info icon={Printer} label="Fax" value={company.fax} />
          <Info icon={Phone} label="Provider relations" value={company.provider_relations_phone} />
          <Info icon={Mail} label="Email" value={company.email} />
          <Info icon={Globe} label="Website" value={company.website} />
          <Info icon={MapPin} label="Address" value={[company.address, company.city, company.state, company.zip].filter(Boolean).join(", ")} />
          {company.notes && <div className="rounded-xl bg-[#F5F2EB] px-4 py-3 text-sm text-[#6D4C41]">{company.notes}</div>}
        </Card>

        <Card className="p-6" data-testid="company-claims-info">
          <h3 className="font-heading text-sm font-bold text-[#3E2723] mb-4 flex items-center gap-2"><Send size={15} className="text-[#7B6157]" /> Claims &amp; Submission</h3>
          <div className="space-y-4">
            <KV label="Submission method" value={company.submission_method} />
            <KV label="Electronic payer ID (EDI)" value={company.electronic_payer_id} />
            <KV label="Clearinghouse" value={company.clearinghouse} />
            <KV label="Payer portal" value={company.payer_portal_url} />
            <KV label="Claims mailing address" value={[company.claims_address, company.claims_city, company.claims_state, company.claims_zip].filter(Boolean).join(", ")} />
          </div>
        </Card>

        <Card className="p-6" data-testid="company-auth-billing-info">
          <h3 className="font-heading text-sm font-bold text-[#3E2723] mb-4 flex items-center gap-2"><ShieldCheck size={15} className="text-[#7B6157]" /> Authorizations &amp; Eligibility</h3>
          <div className="space-y-4">
            <KV label="Requires prior authorization" value={company.requires_authorization ? (company.requires_authorization === "yes" ? "Yes" : "No") : ""} />
            <KV label="Authorization phone / fax" value={[company.auth_phone, company.auth_fax].filter(Boolean).join(" · ")} />
            <KV label="Eligibility phone" value={company.eligibility_phone} />
          </div>
          <h3 className="font-heading text-sm font-bold text-[#3E2723] mt-6 mb-3 flex items-center gap-2"><Banknote size={15} className="text-[#7B6157]" /> Billing Configuration</h3>
          <div className="space-y-3">
            <KV label="Timely filing limit" value={company.timely_filing_days ? `${company.timely_filing_days} days` : ""} />
            <div className="flex flex-wrap gap-2">
              <YesNoChip label="ERA enrolled" value={company.era_enrolled} />
              <YesNoChip label="EFT enabled" value={company.eft_enabled} />
            </div>
          </div>
        </Card>
      </div>

      <Card data-testid="company-insured-clients">
        <div className="p-5 pb-0 flex items-center justify-between">
          <h3 className="font-heading text-sm font-bold text-[#3E2723]">Insured Clients</h3>
          <span className="rounded-full bg-[#4E342E] text-white text-xs font-bold px-2.5 py-0.5">{company.insured_clients?.length || 0}</span>
        </div>
        <div className="p-3">
          <Table headers={["Client", "MRN", "Policy #", "Group #", "Status"]}>
            {(company.insured_clients || []).map((c) => (
              <tr key={c.id} data-testid={`insured-client-${c.id}`} onClick={() => navigate(`/clients/${c.id}`)} className="cursor-pointer hover:bg-[#FDFCF8]">
                <Td className="font-semibold">{c.first_name} {c.last_name}</Td>
                <Td>{c.mrn}</Td>
                <Td>{c.insurance?.policy_number}</Td>
                <Td>{c.insurance?.group_number}</Td>
                <Td><Badge value={c.status} /></Td>
              </tr>
            ))}
          </Table>
          {(!company.insured_clients || company.insured_clients.length === 0) && <Empty text="No insured clients linked" />}
        </div>
      </Card>

      <SlidePanel open={panel} onClose={() => setPanel(false)} title="Edit Insurance Company"
        subtitle="Payer identity, claims routing, authorizations & billing configuration" width="max-w-2xl" testId="company-slide-panel">
        <CompanyForm form={form} setForm={setForm} onSubmit={save} onCancel={() => setPanel(false)} busy={busy} editId={id} />
      </SlidePanel>
    </div>
  );
}
